/*
 * CHI-20: state and flow for /events/:slug/tickets. Presentation lives in
 * components/tickets/*. All server calls go through lib/ticketingApi.
 */
import { useTicketingApiWiring } from "@/lib/ticketingApiProvider";
import { useEffect, useRef, useState } from "react";
import { closeReservedCheckoutWindow, handoffToCheckout, reserveCheckoutWindow } from "@/lib/checkoutWindow";
import {
  attendeeCheckoutErrorMessage,
  classifyCheckoutError,
  getTicketingApi,
  watchTicketQuote,
  clearTicketJoinInterests,
  readTicketJoinInterest,
  writeTicketJoinInterest,
  MAX_TICKETS_PER_ORDER,
  TICKETS_CANCELLED_PARAM,
  TICKETS_CANCELLED_VALUE,
  TICKETS_ORDER_PARAM,
  promotionCheckoutErrorMessage,
  type Person,
  type TicketPromotionQuote,
  type TicketHoldStatus,
  type TicketQuote,
} from "@/lib/ticketingApi";
import {
  countErrors,
  isValidEmail,
  loadTicketDraft,
  resizeAttendees,
  saveTicketDraft,
  validateAttendees,
  type AttendeeRowErrors,
} from "@/lib/ticketAttendees";

import { useBuyerOtp } from "@/hooks/useBuyerOtp";
import { useBuyerEmailRecognition } from "@/hooks/useBuyerEmailRecognition";
import { trpc } from "@/lib/trpc";
import { formatGuestPhone, guestProfileErrors as getGuestProfileErrors, isGuestPurchaseVerified, normalizeGuestEmail, normalizeGuestPhone, validGuestEmail } from "@/lib/guestTicketProfile";
export type { OtpPhase } from "@/hooks/useBuyerOtp";

type ActiveTicketHold = TicketHoldStatus & { orderId: number; receivedAt: number };

function responseClockAnchor(requestStartedAt: number, responseReceivedAt: number) {
  return requestStartedAt + Math.max(0, responseReceivedAt - requestStartedAt) / 2;
}

export function useTicketPurchase(slug: string, buyer: { name?: string | null; email?: string | null } | null | undefined, isLoggedIn: boolean) {
  useTicketingApiWiring();
  const api = getTicketingApi();
  const [quote, setQuote] = useState<TicketQuote | null>(null);
  const [quoteForSlug, setQuoteForSlug] = useState("");
  const [quoteError, setQuoteError] = useState(false);
  const [rows, setRows] = useState<Person[]>(() => [{ name: buyer?.name ?? "", email: buyer?.email ?? "" }]);
  const stashRef = useRef<Person[]>([]);
  const [errors, setErrors] = useState<AttendeeRowErrors[] | null>(null);
  const [restored, setRestored] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState(false);
  /** CHI-20: kill switch (sales closed; button disabled) or rate limit (retry shortly). From classifyCheckoutError. */
  const [checkoutBlock, setCheckoutBlock] = useState<"checkout_disabled" | "rate_limited" | null>(null);
  const [unavailableReason, setUnavailableReason] = useState<string | null>(null);
  /** tickets_attendee_already_ticketed: a plain sentence, naming the attendee when the server does. */
  const [attendeeTicketedMessage, setAttendeeTicketedMessage] = useState<string | null>(null);
  const [hold, setHold] = useState<ActiveTicketHold | null>(null);
  const holdRef = useRef<ActiveTicketHold | null>(null);
  const apiRef = useRef(api);
  apiRef.current = api;
  const [holdExpired, setHoldExpired] = useState(false);
  const [holdEndedMessage, setHoldEndedMessage] = useState<string | null>(null);
  const [holdMessage, setHoldMessage] = useState<string | null>(null);
  const [holdAction, setHoldAction] = useState<"renew" | "expire" | "cancel" | null>(null);
  const [holdPaid, setHoldPaid] = useState(false);
  const [holdRestartRequired, setHoldRestartRequired] = useState(false);
  const holdActionLock = useRef(false);
  const holdPollLock = useRef(false);
  const expireQueued = useRef(false);
  const holdResponseVersion = useRef(0);
  const [atMaxNotice, setAtMaxNotice] = useState(false);
  const [promotionInput, setPromotionInput] = useState("");
  const [requestedPromotionCode, setRequestedPromotionCode] = useState<string | null>(null);
  const [promotionQuote, setPromotionQuote] = useState<TicketPromotionQuote | null>(null);
  const [promotionError, setPromotionError] = useState<string | null>(null);
  const [promotionLoading, setPromotionLoading] = useState(false);
  const [promotionQuoteSignature, setPromotionQuoteSignature] = useState("");
  const [promotionAttempt, setPromotionAttempt] = useState(0);
  const promotionRequest = useRef(0);
  const buyerOtp = useBuyerOtp({ returnTo: `/events/${slug}/tickets` });
  const events: any = trpc.events;
  const guestSessionQuery = events.ticketGuestSession.useQuery(undefined, { retry: false, enabled: !isLoggedIn });
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [guestFieldsSubmitted, setGuestFieldsSubmitted] = useState(false);
  const didRestore = useRef(false);
  const initialGuestSessionChecked = useRef(false);
  const buyerEmailEdited = useRef(false);
  const guestSessionRequest = useRef(0);
  const currentBuyerEmail = useRef(normalizeGuestEmail(rows[0]?.email ?? ""));
  currentBuyerEmail.current = normalizeGuestEmail(rows[0]?.email ?? "");
  const joinInterestSlug = useRef(slug);
  const [joinAfterCheckout, setJoinAfterCheckoutState] = useState(() => readTicketJoinInterest(slug));

  function commitHold(next: ActiveTicketHold | null) {
    holdRef.current = next;
    setHold(next);
  }

  function endHold(orderId: number, status: "paid" | "expired" | "canceled" | "failed", message?: string) {
    if (holdRef.current?.orderId !== orderId) return;
    commitHold(null);
    setHoldMessage(null);
    if (status === "paid") {
      setHoldPaid(true);
      setHoldExpired(false);
      setHoldRestartRequired(false);
      setHoldEndedMessage(message ?? "Payment was confirmed in another tab. This checkout is complete.");
      return;
    }
    setHoldPaid(false);
    setHoldRestartRequired(false);
    setHoldExpired(true);
    const endedMessage = message ?? (status === "expired"
      ? "Your hold has ended. These seats and this price are no longer held for you."
      : status === "canceled"
        ? "This checkout was canceled and is no longer active. Start a new checkout if you'd like to try again."
        : "This checkout is no longer active. Start a new checkout to try again.");
    setHoldEndedMessage(endedMessage);
  }

  function reconcileHoldStatus(orderId: number, response: TicketHoldStatus, clientClockAnchor = Date.now()) {
    const current = holdRef.current;
    if (!current || current.orderId !== orderId) return;
    const status = String(response.status ?? "").toLowerCase();
    if (status === "paid" || status === "expired" || status === "canceled" || status === "failed") {
      endHold(orderId, status);
      return;
    }
    if (status !== "pending" && status !== "closing" && status !== "processing") {
      setHoldMessage("We couldn't confirm the checkout status. Your hold remains unconfirmed while we keep checking.");
      return;
    }
    const validDates = Number.isFinite(Date.parse(response.expiresAt)) && Number.isFinite(Date.parse(response.serverNow));
    const next: ActiveTicketHold = {
      ...current,
      status,
      ...(response.quantity !== undefined ? { quantity: response.quantity } : {}),
      ...(validDates ? { expiresAt: response.expiresAt, serverNow: response.serverNow, receivedAt: clientClockAnchor } : {}),
    };
    commitHold(next);
    setHoldPaid(false);
    if (status === "processing") {
      setHoldRestartRequired(false);
      setHoldMessage("Checking payment status. This checkout is still being verified.");
      return;
    }
    const deadlineAdvanced = validDates && Date.parse(response.expiresAt) > Date.parse(current.expiresAt) + 500;
    if (status === "closing") {
      setHoldMessage("The payment provider has not confirmed checkout closure yet. We can't confirm the hold is released; retry the closure check.");
    } else if (!validDates) {
      setHoldMessage("The server returned an incomplete hold update. Your existing deadline is unchanged; retry the status check.");
    } else if (current.status === "closing" || deadlineAdvanced) {
      setHoldMessage(null);
      if (deadlineAdvanced) {
        setHoldRestartRequired(false);
        setHoldExpired(false);
        setHoldEndedMessage(null);
      }
    }
  }

  function holdDeadlineReached(current: ActiveTicketHold) {
    const serverNow = Date.parse(current.serverNow);
    if (!Number.isFinite(serverNow)) return false;
    const estimatedServerNow = serverNow + Math.max(0, Date.now() - current.receivedAt);
    return Date.parse(current.expiresAt) <= estimatedServerNow;
  }

  useEffect(() => {
    if (joinInterestSlug.current === slug) return;
    joinInterestSlug.current = slug;
    setJoinAfterCheckoutState(readTicketJoinInterest(slug));
  }, [slug]);

  function setJoinAfterCheckout(value: boolean) {
    setJoinAfterCheckoutState(value);
    writeTicketJoinInterest(slug, value);
  }

  const remaining = quote?.seatsLeft ?? null;
  const quoteUnavailable = quote?.available === false;
  const maxQuantity = Math.max(1, Math.min(MAX_TICKETS_PER_ORDER, quote?.maxPerOrder ?? MAX_TICKETS_PER_ORDER, remaining ?? Infinity));
  const soldOut = remaining === 0;
  const emailRecognition = useBuyerEmailRecognition(rows[0]?.email ?? "", !isLoggedIn);
  const guestProfile = { name: rows[0]?.name ?? "", email: normalizeGuestEmail(rows[0]?.email ?? ""), companyName, jobTitle, phone };
  const guestProfileErrors = getGuestProfileErrors(guestProfile);
  const guestProfileValid = Object.values(guestProfileErrors).every(error => !error);
  const guestVerified = isGuestPurchaseVerified({ guestCodeVerified: buyerOtp.phase === "guest_verified", profileValid: guestProfileValid, emailRecognition: emailRecognition.status });
  const verified = isLoggedIn || buyerOtp.phase === "verified" || guestVerified;
  const quoteSignature = quote && quoteForSlug === slug
    ? JSON.stringify([slug, rows.length, quote.unitPriceCents, quote.fullPriceCents, quote.currency, quote.promoActive, quote.promoEndsAt])
    : "";
  const promotionCurrent = Boolean(requestedPromotionCode && promotionQuote && promotionQuoteSignature === quoteSignature);
  const promotionNeedsAction = Boolean(promotionInput.trim() && !promotionCurrent);

  // Restore after a cancelled Stripe checkout: server pending order first, same-tab storage fallback.
  useEffect(() => {
    const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    if (params.get(TICKETS_CANCELLED_PARAM) !== TICKETS_CANCELLED_VALUE) return;
    const orderId = params.get(TICKETS_ORDER_PARAM);
    let live = true;
    const apply = (attendees: Person[]) => {
      if (!live || attendees.length < 1) return;
      didRestore.current = true;
      stashRef.current = [];
      setRows(attendees.slice(0, MAX_TICKETS_PER_ORDER).map(a => ({ name: String(a.name ?? ""), email: String(a.email ?? "") })));
      setRestored(true);
    };
    (async () => {
      const id = orderId && /^\d+$/.test(orderId) ? Number(orderId) : null;
      if (id !== null) {
        try {
          const order = await api.ticketOrderForRestore({ orderId: id });
          if (order && order.eventSlug === slug && Array.isArray(order.attendees) && order.attendees.length) {
            apply(order.attendees.map(a => ({ name: a.name ?? "", email: a.email ?? "" })));
            restorePromotionCode(order.promotionCode);
            void api.releaseTicketCheckout({ orderId: id }).catch(() => undefined);
            return;
          }
        } catch {
          /* NOT_FOUND or network: fall through to the same-tab fallback */
        }
      }
      const draft = loadTicketDraft(slug, MAX_TICKETS_PER_ORDER);
      if (draft) apply(draft.attendees);
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Buyer can load after first render; fill attendee 1 only if untouched.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !buyer || didRestore.current) return;
    prefilled.current = true;
    setRows(current => {
      const [first, ...rest] = current;
      if (first && (first.name || first.email)) return current;
      return [{ name: buyer.name ?? "", email: buyer.email ?? "" }, ...rest];
    });
  }, [buyer]);

  function resumeOwnGuestSession(profile: unknown, targetEmail: string) {
    if (!profile || !validGuestEmail(targetEmail) || currentBuyerEmail.current !== targetEmail) return;
    const guest = profile as { email?: unknown; name?: unknown; companyName?: unknown; jobTitle?: unknown; phone?: unknown };
    if (typeof guest.email !== "string" || normalizeGuestEmail(guest.email) !== targetEmail) return;
    if (![guest.name, guest.companyName, guest.jobTitle, guest.phone].every(value => typeof value === "string" && value.trim())) return;
    setRows(current => current.map((row, index) => index === 0 ? { ...row, name: String(guest.name), email: targetEmail } : row));
    setCompanyName(String(guest.companyName));
    setJobTitle(String(guest.jobTitle));
    setPhone(formatGuestPhone(String(guest.phone)));
    buyerOtp.markGuestVerified();
  }

  // Only apply the initial cookie response before the buyer edits their address.
  // Later email changes explicitly refetch and bind the result to that exact request.
  useEffect(() => {
    if (initialGuestSessionChecked.current || guestSessionQuery.isLoading) return;
    if (!validGuestEmail(currentBuyerEmail.current)) return;
    if (buyerEmailEdited.current) {
      initialGuestSessionChecked.current = true;
      return;
    }
    initialGuestSessionChecked.current = true;
    resumeOwnGuestSession(guestSessionQuery.data, currentBuyerEmail.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestSessionQuery.dataUpdatedAt, rows[0]?.email]);

  useEffect(() => {
    let live = true;
    const stop = watchTicketQuote(slug, q => {
      if (live) { setQuote(q); setQuoteForSlug(slug); setQuoteError(false); }
    }, () => {
      if (live) { setQuote(null); setQuoteForSlug(""); setQuoteError(true); }
    });
    return () => { live = false; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // The server deadline is authoritative. Polling only reconciles cross-tab
  // changes; it never extends a hold or restarts its clock.
  useEffect(() => {
    const orderId = hold?.orderId;
    if (orderId === undefined) return;
    let live = true;
    const poll = async () => {
      if (!live || holdActionLock.current || holdPollLock.current || holdRef.current?.orderId !== orderId) return;
      holdPollLock.current = true;
      const version = holdResponseVersion.current;
      try {
        const requestStartedAt = Date.now();
        const status = await apiRef.current.ticketHoldStatus({ orderId });
        if (live && !holdActionLock.current && version === holdResponseVersion.current) {
          reconcileHoldStatus(orderId, status, responseClockAnchor(requestStartedAt, Date.now()));
        }
      } catch {
        if (live && !holdActionLock.current && version === holdResponseVersion.current && holdRef.current?.orderId === orderId) {
          setHoldMessage(current => current ?? "We couldn't sync the hold with the server. Your seat status is unconfirmed; we'll keep checking.");
        }
      } finally {
        holdPollLock.current = false;
      }
    };
    void poll();
    const interval = setInterval(() => void poll(), 5_000);
    return () => {
      live = false;
      clearInterval(interval);
    };
    // Hold identity is stable across server refreshes; deadline updates must not restart polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hold?.orderId]);

  // Same-tab fallback mirror (spec 3.7, debounced 300ms). Not relied on for the new-tab cancel.
  useEffect(() => {
    const id = setTimeout(() => saveTicketDraft(slug, rows), 300);
    return () => clearTimeout(id);
  }, [slug, rows]);

  function setQuantity(next: number) {
    const q = Math.max(1, Math.min(maxQuantity, Math.floor(next) || 1));
    setAtMaxNotice(next >= maxQuantity && q === maxQuantity);
    const resized = resizeAttendees(rows, stashRef.current, q);
    stashRef.current = resized.stash;
    setRows(resized.rows);
    if (errors) setErrors(validateAttendees(resized.rows));
  }

  function removeGuest(index: number) {
    if (index < 1 || rows.length <= 1) return;
    const next = rows.filter((_, i) => i !== index);
    setRows(next);
    setAtMaxNotice(false);
    if (errors) setErrors(validateAttendees(next));
  }

  function updateRow(index: number, field: keyof Person, value: string) {
    const next = rows.map((row, i) => (i === index ? { ...row, [field]: value } : row));
    setRows(next);
    if (errors) setErrors(validateAttendees(next));
    if (index === 0 && field === "email") {
      buyerEmailEdited.current = true;
      const targetEmail = normalizeGuestEmail(value);
      if (targetEmail !== currentBuyerEmail.current) {
        clearTicketJoinInterests();
        setJoinAfterCheckoutState(false);
      }
      currentBuyerEmail.current = targetEmail;
      const request = ++guestSessionRequest.current;
      buyerOtp.reset();
      setCompanyName("");
      setJobTitle("");
      setPhone("");
      if (validGuestEmail(targetEmail) && !isLoggedIn) {
        void guestSessionQuery.refetch().then((result: { data?: unknown }) => {
          if (request !== guestSessionRequest.current || currentBuyerEmail.current !== targetEmail) return;
          resumeOwnGuestSession(result.data, targetEmail);
        }).catch(() => undefined);
      }
    } else if (index === 0 && field === "name" && buyerOtp.phase === "guest_verified") {
      buyerOtp.reset();
    }
  }

  function validateOnBlur() {
    setErrors(validateAttendees(rows));
  }

  async function sendCode() {
    if (!isValidEmail(rows[0].email)) return;
    const latestEmail = normalizeGuestEmail(rows[0].email);
    setErrors(validateAttendees(rows));
    if (emailRecognition.status === "member" && !emailRecognition.signInRequired) {
      await buyerOtp.send(latestEmail);
      return;
    }
    if (emailRecognition.status !== "guest") return;
    setGuestFieldsSubmitted(true);
    if (!guestProfileValid) {
      return;
    }
    await buyerOtp.sendGuest({ ...guestProfile, phone: normalizeGuestPhone(phone) ?? phone });
  }

  function verifyCode(code: string) {
    if (buyerOtp.phase === "totp") return buyerOtp.verifyAuthenticator(code);
    if (emailRecognition.status === "guest") return buyerOtp.verifyGuestCode({ ...guestProfile, phone: normalizeGuestPhone(phone) ?? phone }, code);
    return buyerOtp.verify(rows[0].email, code);
  }

  function changeEmail() {
    buyerOtp.reset();
  }

  function updateGuestField(field: "companyName" | "jobTitle" | "phone", value: string) {
    if (buyerOtp.phase === "guest_verified") buyerOtp.reset();
    if (field === "companyName") setCompanyName(value);
    if (field === "jobTitle") setJobTitle(value);
    if (field === "phone") setPhone(formatGuestPhone(value));
  }

  // Every cart/automatic-quote change gets a fresh server quote. Responses are tied to
  // the exact request identity, so stale cents cannot reappear after a change.
  useEffect(() => {
    if (!requestedPromotionCode || !quote || !quoteSignature) {
      setPromotionLoading(false);
      setPromotionQuote(null);
      setPromotionQuoteSignature("");
      return;
    }
    if (!verified) {
      setPromotionLoading(false);
      setPromotionQuote(null);
      setPromotionQuoteSignature("");
      setPromotionError("Verify your email above to apply a promotion code.");
      return;
    }
    const requestId = ++promotionRequest.current;
    setPromotionLoading(true);
    setPromotionError(null);
    setPromotionQuote(null);
    setPromotionQuoteSignature("");
    const timer = setTimeout(() => {
      api.ticketPromotionQuote({ slug, code: requestedPromotionCode, quantity: rows.length }).then(result => {
        if (promotionRequest.current !== requestId) return;
        setPromotionQuote(result);
        setPromotionQuoteSignature(quoteSignature);
        setPromotionInput(result.code || requestedPromotionCode);
        setPromotionError(null);
        setPromotionLoading(false);
      }).catch(error => {
        if (promotionRequest.current !== requestId) return;
        setPromotionQuote(null);
        setPromotionQuoteSignature("");
        setPromotionError(promotionCheckoutErrorMessage(error) ?? "We couldn't apply that code. Check it and try again.");
        setPromotionLoading(false);
      });
    }, 250);
    return () => {
      clearTimeout(timer);
      if (promotionRequest.current === requestId) promotionRequest.current++;
    };
  // api is a module-level adapter and rows.length is the quote quantity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, rows.length, quoteSignature, requestedPromotionCode, promotionAttempt, verified]);

  function editPromotionCode(value: string) {
    setPromotionInput(value.slice(0, 64));
    setRequestedPromotionCode(null);
    setPromotionQuote(null);
    setPromotionQuoteSignature("");
    setPromotionError(null);
    setPromotionLoading(false);
    promotionRequest.current++;
  }

  function applyPromotionCode() {
    const code = promotionInput.trim();
    if (!code) {
      setRequestedPromotionCode(null);
      setPromotionError(null);
      return;
    }
    if (code.length > 64 || !/^[a-zA-Z0-9_-]+$/.test(code)) {
      setPromotionError("Use up to 64 letters, numbers, underscores, or hyphens.");
      setRequestedPromotionCode(null);
      setPromotionQuote(null);
      return;
    }
    if (!quote) {
      setPromotionError("Ticket pricing is still loading. Try again in a moment.");
      return;
    }
    setPromotionInput(code);
    setRequestedPromotionCode(code);
    setPromotionAttempt(current => current + 1);
    setPromotionQuote(null);
    setPromotionQuoteSignature("");
    setPromotionError(null);
    setPromotionLoading(true);
  }

  function removePromotionCode() {
    setPromotionInput("");
    setRequestedPromotionCode(null);
    setPromotionQuote(null);
    setPromotionQuoteSignature("");
    setPromotionError(null);
    setPromotionLoading(false);
    promotionRequest.current++;
  }

  function restorePromotionCode(code: string | null | undefined) {
    if (!code) return;
    const normalized = code.trim();
    if (normalized.length > 64 || !/^[a-zA-Z0-9_-]+$/.test(normalized)) return;
    setPromotionInput(normalized);
    setRequestedPromotionCode(normalized);
    setPromotionAttempt(current => current + 1);
    setPromotionQuote(null);
    setPromotionQuoteSignature("");
  }

  /** Returns the number of invalid fields (0 = proceeding to checkout). */
  async function submit(): Promise<number> {
    const found = validateAttendees(rows);
    setErrors(found);
    const n = countErrors(found);
    if (n > 0 || !verified || (guestVerified && !guestProfileValid) || soldOut || checkoutBlock === "checkout_disabled" || promotionNeedsAction || promotionLoading) return n;
    setSubmitting(true);
    setCheckoutError(false);
    setCheckoutBlock(null);
    setAttendeeTicketedMessage(null);
    setUnavailableReason(null);
    setHoldExpired(false);
    setHoldEndedMessage(null);
    setHoldMessage(null);
    setHoldPaid(false);
    setHoldRestartRequired(false);
    const attendees = rows.map(r => ({ name: r.name.trim(), email: r.email.trim() }));
    const reserved = reserveCheckoutWindow();
    const checkoutRequestStartedAt = Date.now();
    try {
      // Contract §3: slug + attendees only (quantity = attendees.length). Never a price.
      const result = await api.ticketCheckout({
        eventSlug: slug,
        attendees,
        ...(promotionCurrent && requestedPromotionCode ? { promotionCode: requestedPromotionCode } : {}),
      });
      if (result.status === "unavailable") {
        closeReservedCheckoutWindow(reserved);
        setUnavailableReason(result.reason || null);
        setSubmitting(false);
        return 0;
      }
      handoffToCheckout(result.checkoutUrl, reserved);
      setSubmitting(false);
      // v4: HoldTimer from the server's expiresAt/serverNow.
      commitHold({
        orderId: result.orderId,
        status: "pending",
        expiresAt: result.expiresAt,
        serverNow: result.serverNow,
        receivedAt: responseClockAnchor(checkoutRequestStartedAt, Date.now()),
      });
    } catch (error) {
      closeReservedCheckoutWindow(reserved);
      const kind = classifyCheckoutError(error);
      const promotionMessage = promotionCheckoutErrorMessage(error);
      // tickets_pending_order_exists (409) keeps the existing inline error, like any other failure.
      if (kind === "checkout_disabled" || kind === "rate_limited") setCheckoutBlock(kind);
      else if (attendeeCheckoutErrorMessage(error)) setAttendeeTicketedMessage(attendeeCheckoutErrorMessage(error));
      else if (promotionMessage && promotionCurrent && requestedPromotionCode) {
        promotionRequest.current++;
        setRequestedPromotionCode(null);
        setPromotionQuote(null);
        setPromotionQuoteSignature("");
        setPromotionLoading(false);
        setPromotionError(promotionMessage);
      } else setCheckoutError(true);
      setSubmitting(false);
    }
    return 0;
  }

  async function onHoldExpire() {
    const current = holdRef.current;
    if (!current) return;
    if (holdActionLock.current) {
      expireQueued.current = true;
      return;
    }
    holdResponseVersion.current++;
    holdActionLock.current = true;
    setHoldAction("expire");
    setHoldMessage(null);
    try {
      const requestStartedAt = Date.now();
      const response = await apiRef.current.expireTicketHold({ orderId: current.orderId });
      reconcileHoldStatus(current.orderId, response, responseClockAnchor(requestStartedAt, Date.now()));
      const stillCurrent = holdRef.current;
      if (stillCurrent?.orderId === current.orderId && response.status === "pending" && holdDeadlineReached(stillCurrent)) {
        setHoldMessage("The server still reports an active checkout after its deadline. Retry the status check; the hold is not confirmed released.");
      }
    } catch {
      if (holdRef.current?.orderId === current.orderId) {
        setHoldMessage("We couldn't confirm whether checkout expired or the hold was released. Your seat status is unconfirmed; retry the status check.");
      }
    } finally {
      holdActionLock.current = false;
      setHoldAction(null);
      if (expireQueued.current) {
        expireQueued.current = false;
        void onHoldExpire();
      }
    }
  }

  async function renewHold() {
    const current = holdRef.current;
    if (!current || holdActionLock.current || holdRestartRequired || current.status === "closing" || current.status === "processing") return;
    holdResponseVersion.current++;
    holdActionLock.current = true;
    setHoldAction("renew");
    setHoldMessage(null);
    try {
      const requestStartedAt = Date.now();
      const response = await apiRef.current.renewTicketHold({ orderId: current.orderId });
      reconcileHoldStatus(current.orderId, response, responseClockAnchor(requestStartedAt, Date.now()));
      if (response.status === "pending" && holdRef.current?.orderId === current.orderId) {
        const duration = Date.parse(response.expiresAt) - Date.parse(response.serverNow);
        if (!Number.isFinite(duration) || duration <= 0) {
          setHoldMessage("The server did not confirm a new hold deadline. Your existing deadline remains in effect; retry or cancel checkout.");
        } else {
          setHoldMessage(null);
          setHoldExpired(false);
          setHoldEndedMessage(null);
          setHoldPaid(false);
          setHoldRestartRequired(false);
        }
      }
    } catch (error) {
      const candidate = error as { code?: unknown; data?: { code?: unknown } };
      const code = candidate?.data?.code ?? candidate?.code;
      const restartRequired = code === "PRECONDITION_FAILED";
      if (holdRef.current?.orderId === current.orderId) {
        setHoldRestartRequired(restartRequired);
        setHoldMessage(restartRequired
          ? "This checkout can't be extended. Cancel this order and restart checkout for a fresh 8-minute hold."
          : "We couldn't confirm the extension. We'll re-check with the server; don't assume the renewal succeeded. Retry or cancel checkout.");
      }
      if (restartRequired) {
        try {
          const requestStartedAt = Date.now();
          const status = await apiRef.current.ticketHoldStatus({ orderId: current.orderId });
          reconcileHoldStatus(current.orderId, status, responseClockAnchor(requestStartedAt, Date.now()));
          if (holdRef.current?.orderId === current.orderId) {
            setHoldRestartRequired(true);
            setHoldMessage("This checkout can't be extended. Cancel this order and restart checkout for a fresh 8-minute hold.");
          }
        } catch {
          // Keep the restart guidance and current server-provided deadline; no extension is assumed.
        }
      }
    } finally {
      holdActionLock.current = false;
      setHoldAction(null);
      if (expireQueued.current) {
        expireQueued.current = false;
        void onHoldExpire();
      }
    }
  }

  async function cancelHold() {
    const current = holdRef.current;
    if (!current || holdActionLock.current) return;
    holdResponseVersion.current++;
    holdActionLock.current = true;
    setHoldAction("cancel");
    setHoldMessage(null);
    try {
      const result = await apiRef.current.releaseTicketCheckout({ orderId: current.orderId });
      if (holdRef.current?.orderId !== current.orderId) return;
      if (result.released > 0) {
        endHold(current.orderId, "canceled", "Your checkout was canceled and the server confirmed the hold was released.");
        return;
      }
      const requestStartedAt = Date.now();
      const status = await apiRef.current.ticketHoldStatus({ orderId: current.orderId });
      reconcileHoldStatus(current.orderId, status, responseClockAnchor(requestStartedAt, Date.now()));
      if (holdRef.current?.orderId === current.orderId) {
        setHoldMessage("The server has not confirmed cancellation or release yet. Keep this checkout open and retry Cancel order.");
      }
    } catch {
      if (holdRef.current?.orderId === current.orderId) {
        try {
          const requestStartedAt = Date.now();
          const status = await apiRef.current.ticketHoldStatus({ orderId: current.orderId });
          reconcileHoldStatus(current.orderId, status, responseClockAnchor(requestStartedAt, Date.now()));
        } catch {
          // Keep the hold context; a network failure is never proof that seats were released.
        }
        if (holdRef.current?.orderId === current.orderId) {
          setHoldMessage("We couldn't confirm cancellation or release. Your hold may still be active; keep this checkout open and retry.");
        }
      }
    } finally {
      holdActionLock.current = false;
      setHoldAction(null);
      if (expireQueued.current) {
        expireQueued.current = false;
        void onHoldExpire();
      }
    }
  }

  const otp = { phase: buyerOtp.phase, prefill: buyerOtp.prefill };

  return {
    quote, quoteError, rows, errors, restored, dismissRestored: () => setRestored(false), submitting, checkoutError, checkoutBlock, attendeeTicketedMessage, unavailableReason,
    hold, holdExpired, holdEndedMessage, holdMessage, holdAction, holdPaid, holdRestartRequired,
    onHoldExpire, renewHold, cancelHold, quoteUnavailable, atMaxNotice, maxQuantity, remaining, soldOut, verified, otp,
      quantity: rows.length, setQuantity, removeGuest, updateRow, validateOnBlur, sendCode, verifyCode, changeEmail, submit,
      emailRecognition, guestProfile: { companyName, jobTitle, phone }, guestProfileErrors, guestProfileValid, updateGuestField,
      guestFieldsSubmitted,
      joinAfterCheckout, setJoinAfterCheckout,
     promotionInput, promotionQuote: promotionCurrent ? promotionQuote : null, promotionError, promotionLoading, promotionNeedsAction,
     editPromotionCode, applyPromotionCode, removePromotionCode,
  };
}
